import { styled, Typography } from "@mui/material";

const sizes = {
  sm: { width: "2.75rem", height: "1.5rem", inset: "0.125rem", ring: "1px", font: "sm" },
  md: { width: "3.25rem", height: "1.75rem", inset: "0.125rem", ring: "1px", font: "md" },
  lg: { width: "4rem", height: "2.25rem", inset: "0.1875rem", ring: "2px", font: "lg" },
} as const;

export type ToggleSize = keyof typeof sizes;

const thumb = "calc(var(--toggle-height) - var(--toggle-inset) * 2)";
const travel = "calc(var(--toggle-width) - var(--toggle-height))";

export const ToggleContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "toggleSize",
})<{ toggleSize: ToggleSize }>(({ theme, toggleSize }) => ({
  "--toggle-width": sizes[toggleSize].width,
  "--toggle-height": sizes[toggleSize].height,
  "--toggle-inset": sizes[toggleSize].inset,
  "--toggle-ring": sizes[toggleSize].ring,
  "--toggle-font": theme.fontSizes[sizes[toggleSize].font],

  display: "inline-flex",
  alignItems: "center",
  gap: theme.space._200,
  minWidth: 0,
}));

export const ToggleTrack = styled("button", {
  shouldForwardProp: (prop) => prop !== "on",
})<{ on: boolean }>(({ theme, on }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: on ? "flex-start" : "flex-end",
  flexShrink: 0,
  width: "var(--toggle-width)",
  height: "var(--toggle-height)",
  padding: "0 var(--toggle-inset)",
  border: "none",
  borderRadius: "999px",
  cursor: "pointer",
  backgroundColor: on ? theme.colors["lime.500"] : theme.colors["neutral.800"],
  color: on ? theme.colors["lime.700"] : theme.colors["neutral.500"],
  transition: "background-color 200ms ease-in-out, color 200ms ease-in-out",

  "&:disabled": { cursor: "not-allowed", opacity: theme.opacity.medium },
  "&:not(:disabled):hover": { backgroundColor: on ? theme.colors["lime.400"] : theme.colors["neutral.700"] },
  "&:focus-visible": { outline: `2px solid ${theme.colors["lime.400"]}`, outlineOffset: "2px" },
}));

export const ToggleThumb = styled("span", {
  shouldForwardProp: (prop) => prop !== "on",
})<{ on: boolean }>(({ theme, on }) => ({
  position: "absolute",
  top: "50%",
  left: "var(--toggle-inset)",
  boxSizing: "border-box",
  width: thumb,
  height: thumb,
  borderRadius: "50%",
  backgroundColor: theme.colors["neutral.200"],
  border: on ? `var(--toggle-ring) solid ${theme.colors["lime.700"]}` : "none",
  transform: on ? `translate(${travel}, -50%)` : "translateY(-50%)",
  transition: "transform 200ms ease-in-out",
}));

export const ToggleIcon = styled("span")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: travel,
  pointerEvents: "none",
});

export const ToggleLabel = styled(Typography)(({ theme }) => ({
  fontFamily: theme.fonts.default,
  fontSize: "var(--toggle-font)",
  color: theme.colors["neutral.500"],
  minWidth: 0,
}));
